import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import platform from '@propertypal/shared/src/utils/platform';
import { validEmail, validPhoneCharacters } from '@propertypal/shared/src/utils/regex';
import Button from '@propertypal/web-ui/src/buttons/Button';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import { Column } from '@propertypal/web-ui/src/layout';
import Recaptcha, { getCaptchaKey } from '@propertypal/web-ui/src/security/Recaptcha';
import { openToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { Bold, SubHeading, Text } from '@propertypal/web-ui/src/typography';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import * as yup from 'yup';
import { SentContainer } from '../estate-agent-websites/GetInTouch.style';
import TextContainer, { Title } from './AgentSignUpForm.style';

const validationSchema = yup.object().shape({
  name: yup.string().required('Please enter your name'),
  companyName: yup.string().required('Please enter your company name'),
  phoneNumber: yup
    .string()
    .matches(validPhoneCharacters, 'Please enter a valid phone number')
    .required('Please enter your phone number'),
  email: yup
    .string()
    .matches(validEmail, 'Please enter a valid email address')
    .required('Please enter your email address'),
});

const initialValues = {
  name: '',
  companyName: '',
  phoneNumber: '',
  email: '',
};

const AgentSignUpForm = () => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<boolean>(false);
  const theme = useTheme();
  const dispatch = useDispatch();

  const onFormSubmit = async (values: any) => {
    setSending(true);

    try {
      const recaptchaKey = await getCaptchaKey('enquire');
      const body = {
        name: values.name,
        companyName: values.companyName,
        email: values.email.trim(),
        telephone: values.phoneNumber,
        comments: 'N/A',
        recaptcha: recaptchaKey,
        reason: `I want to list my properties on ${platform.domain}`,
      };
      const res = await axios(
        {
          method: 'POST',
          url: `${API_URL}/agent-enquire`,
          data: body,
        },
        dispatch,
      );
      if (res) setSent(true);
    } catch {
      openToast({
        title: 'Error',
        message: "Something went wrong. We couldn't send your enquiry. Please try again!",
        type: 'error',
      });
    }
    setSending(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      onFormSubmit(values);
    },
  });

  return (
    <div>
      <Recaptcha />

      <Title>New Estate Agents Sign Up</Title>

      {sent ? (
        <SentContainer
          id="contact"
          data-testid="contact-sent"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 46 }} />
          <SubHeading textTransform="uppercase" mt={20} fontSize={28} fontWeight="600">
            Thanks!
          </SubHeading>
          <Text mt={20} align="center" fontSize={18}>
            Your enquiry has been sent, and a member of the team will be in touch shortly
          </Text>
        </SentContainer>
      ) : (
        <>
          <TextContainer>
            <Text>
              <Bold>Want to join the leading local property portal?</Bold>
            </Text>
            <Text color={theme.textDark}>Contact us below to get signed up.</Text>
          </TextContainer>

          <form onSubmit={formik.handleSubmit}>
            <Column mb={20}>
              <TextInput
                name="name"
                placeholder="Your name"
                value={formik.values.name}
                onValueChange={formik.handleChange('name')}
                error={formik.touched.name && formik.errors.name}
              />
            </Column>

            <Column mb={20}>
              <TextInput
                name="companyName"
                placeholder="Your company name"
                value={formik.values.companyName}
                onValueChange={formik.handleChange('companyName')}
                error={formik.touched.companyName && formik.errors.companyName}
              />
            </Column>

            <Column mb={20}>
              <TextInput
                name="phoneNumber"
                placeholder="Your telephone number"
                value={formik.values.phoneNumber}
                onValueChange={formik.handleChange('phoneNumber')}
                error={formik.touched.phoneNumber && formik.errors.phoneNumber}
              />
            </Column>

            <Column mb={20}>
              <TextInput
                name="email"
                placeholder="Your email address"
                value={formik.values.email}
                onValueChange={formik.handleChange('email')}
                error={formik.touched.email && formik.errors.email}
                type="email"
              />
            </Column>

            <Button loading={sending} testID="signUpSubmit" type="submit">
              Sign Up
            </Button>
          </form>
        </>
      )}
    </div>
  );
};

export default AgentSignUpForm;
